import { translate, useLocale } from "../../localization";
import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { uploadEnvironmentBackgroundAudio } from "../../services/Content";
import { AppButton } from "../ui";
import "./EnvironmentAudioUploader.scss";

const AUDIO_INPUT_ACCEPT = ".mp3,audio/mpeg";

const isMp3File = (file) =>
  Boolean(file) &&
  (file.name?.toLowerCase().endsWith(".mp3") || file.type === "audio/mpeg");

const EnvironmentAudioUploader = ({ environmentId, audioUrl = "", onUploaded }) => {
  useLocale();
  const [confirmedAudioUrl, setConfirmedAudioUrl] = useState(audioUrl);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [feedback, setFeedback] = useState({ message: "", tone: "" });
  const inputRef = useRef(null);
  const dragDepthRef = useRef(0);

  useEffect(() => {
    setConfirmedAudioUrl(audioUrl || "");
  }, [audioUrl]);

  const uploadFile = async (file) => {
    if (!isMp3File(file)) {
      setFeedback({ message: translate("Choose an MP3 file."), tone: "error" });
      return;
    }

    if (!environmentId) {
      setFeedback({
        message: translate("This environment does not have an id for its audio."),
        tone: "error",
      });
      return;
    }

    setUploading(true);
    setFeedback({ message: "", tone: "" });
    let uploadComplete = false;

    try {
      await uploadEnvironmentBackgroundAudio(environmentId, file);
      uploadComplete = true;

      const refreshedAudioUrl = onUploaded ? await onUploaded() : "";
      if (!refreshedAudioUrl) {
        throw new Error(translate("The uploaded audio could not be confirmed yet."));
      }

      setConfirmedAudioUrl(refreshedAudioUrl);
      setFeedback({ message: translate("Background audio updated."), tone: "success" });
    } catch (error) {
      console.error("Error uploading environment background audio:", error);
      setFeedback({
        message: uploadComplete
          ? translate("The MP3 was uploaded, but the refreshed audio could not be confirmed. Reopen this editor to check it.")
          : error?.response?.data?.message ||
            error?.message ||
            translate("Background audio upload failed."),
        tone: uploadComplete ? "warning" : "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;

    if (selectedFiles.length > 1) {
      setFeedback({
        message: translate("Choose one MP3 file at a time."),
        tone: "error",
      });
      return;
    }

    uploadFile(selectedFiles[0]);
  };

  const handleSelected = (event) => {
    handleFiles(event.target.files);
    event.target.value = "";
  };

  const isFileDrag = (event) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = (event) => {
    if (!isFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();

    if (uploading || !environmentId) return;

    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const handleDragOver = (event) => {
    if (!isFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = uploading || !environmentId ? "none" : "copy";
  };

  const handleDragLeave = (event) => {
    if (!dragActive) return;

    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

    if (dragDepthRef.current === 0) {
      setDragActive(false);
    }
  };

  const handleDrop = (event) => {
    if (!isFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);

    if (uploading || !environmentId) return;

    handleFiles(event.dataTransfer.files);
    event.dataTransfer.clearData?.();
  };

  const hasAudio = Boolean(confirmedAudioUrl);

  return (
    <div
      className={`environmentAudioUploader${
        dragActive ? " environmentAudioUploader--dragActive" : ""
      }`}
      role="group"
      aria-label={translate("Environment background audio")}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="environmentAudioUploader__summary">
        <div className="environmentAudioUploader__icon" aria-hidden="true">
          {hasAudio ? <CheckCircleOutlineIcon /> : <MusicNoteIcon />}
        </div>
        <div className="environmentAudioUploader__copy">
          <Typography className="environmentAudioUploader__status" component="div">
            {hasAudio ? translate("Background music uploaded") : translate("No background music uploaded")}
          </Typography>
          <Typography className="environmentAudioUploader__help" component="div">{translate("MP3 audio plays on a loop while this environment is active.")}</Typography>
        </div>
      </div>

      {hasAudio && (
        <audio
          className="environmentAudioUploader__player"
          controls
          preload="metadata"
          src={confirmedAudioUrl}
          aria-label={translate("Background music preview")}
        />
      )}

      <div className="environmentAudioUploader__actions">
        <input
          ref={inputRef}
          hidden
          type="file"
          accept={AUDIO_INPUT_ACCEPT}
          aria-label={translate("Choose environment background audio")}
          onChange={handleSelected}
        />
        <AppButton
          kind={hasAudio ? "secondary" : "primary"}
          size="small"
          startIcon={<UploadFileIcon fontSize="small" />}
          loading={uploading}
          loadingLabel={translate("Uploading")}
          disabled={!environmentId}
          onClick={() => inputRef.current?.click()}
        >
          {hasAudio ? translate("Replace MP3") : translate("Upload MP3")}
        </AppButton>
        <Typography className="environmentAudioUploader__dropHint" component="span">{translate("or drag an MP3 here")}</Typography>
      </div>

      {dragActive && (
        <div className="environmentAudioUploader__dropOverlay" role="status">
          <UploadFileIcon aria-hidden="true" />
          <span>{translate("Drop MP3 to upload")}</span>
        </div>
      )}

      {feedback.message && (
        <Typography
          className={`environmentAudioUploader__feedback environmentAudioUploader__feedback--${feedback.tone}`}
          role={feedback.tone === "error" ? "alert" : "status"}
          component="div"
        >
          {feedback.message}
        </Typography>
      )}
    </div>
  );
};

export default EnvironmentAudioUploader;
